import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Settings, Save, School, Calendar, Bell, Shield,
  Mail, Globe, Palette, Database, Upload, CheckCircle, X,
  ChevronRight, Lock, Eye, EyeOff, RefreshCw, AlertTriangle,
  Plus, Trash2, Edit2, Building2, GraduationCap as AcademicIcon,
  BookOpen, MapPin, Phone,
  User
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { adminApi, authApi, schoolApi } from '@/services/api'
import { toast } from 'sonner'

// ==================== TRANSLATIONS ====================
const TRANSLATIONS = {
  fr: {
    pageTitle: "Parametres de l'etablissement",
    pageSubtitle: "Configuration generale du Lycee Bilingue de Baleng",
    readonlyBadge: "Consultation uniquement",
    saveBtn: "Sauvegarder",
    savingBtn: "Sauvegarde...",
    changeLogo: "Changer le logo",
    tabs: {
      general: 'General',
      academic: 'Academique',
      schools: 'Etablissements',
      notifications: 'Notifications',
      security: 'Securite',
      appearance: 'Apparence',
      system: 'Systeme',
    },
    // General tab
    mySchoolInfo: "Vous modifiez les informations de votre etablissement scolaire.",
    schoolInfo: "Informations de l'etablissement",
    fullName: "Nom complet",
    shortName: "Nom court (acronyme)",
    shortNameHint: 'Enregistre dans le champ "acronym" de la base de donnees',
    foundedYear: "Annee de fondation",
    address: "Adresse",
    city: "Ville",
    country: "Pays",
    phone: "Telephone",
    email: "Email",
    website: "Site web",
    websitePlaceholder: "https://",
    direction: "Direction",
    directorName: "Nom du directeur",
    directorEmail: "Email du directeur",
    legalInfo: "Informations legales",
    accreditationNum: "Numero d'agrement",
    taxId: "Numero fiscal",
    bankInfo: "Informations bancaires",
    bankName: "Banque",
    accountNum: "Numero de compte",
    iban: "IBAN",
    // Academic tab
    mySchoolYears: "Annees scolaires de {name}",
    mySchoolYearsDesc: "Vous visualisez les annees scolaires de votre etablissement.",
    activeYear: "Annee scolaire active",
    academicYears: "Annees scolaires",
    newYear: "Nouvelle annee",
    noYears: "Aucune annee scolaire configuree",
    configAcademic: "Configuration academique",
    startDate: "Debut annee scolaire",
    endDate: "Fin annee scolaire",
    termsCount: "Nombre de trimestres",
    sequencesCount: "Sequences par trimestre",
    semesters: "2 (Semestres)",
    trimesters: "3 (Trimestres)",
    gradingScale: "Bareme de notation",
    maxScale: "Echelle maximale",
    passThreshold: "Seuil de passage",
    honorThreshold: "Seuil tableau d'honneur",
    // Schools tab
    mySchoolBanner: "Mon etablissement",
    mySchoolBannerDesc: "Vous visualisez les informations de votre etablissement scolaire et votre annee scolaire active.",
    mySchoolBannerAdmin: "Vous visualisez votre etablissement et toutes ses annees scolaires.",
    schoolsTitle: "Etablissements scolaires",
    mySchoolTitle: "Mon etablissement",
    newSchool: "Nouvel etablissement",
    noSchools: "Aucun etablissement configure",
    noSchoolsUser: "Aucun etablissement associe a votre compte",
    yearsAllSchools: "Annees scolaires (tous etablissements)",
    yearsMySchool: "Annees scolaires de mon etablissement",
    myYear: "Mon annee scolaire",
    noYearsAvailable: "Aucune annee scolaire disponible",
    active: "Actif",
    inactive: "Inactif",
    mySchoolBadge: "Mon etablissement",
    selected: "Selectionnee",
    setActive: "Definir active",
    // Notifications tab
    preferences: "Preferences",
    emailNotif: "Notifications par email",
    emailNotifDesc: "Envoyer les alertes par email",
    smsNotif: "Notifications SMS",
    smsNotifDesc: "Alertes urgentes par SMS",
    // Security tab
    password: "Mot de passe",
    changePassword: "Changer le mot de passe",
    changePasswordDesc: "Mettez a jour votre mot de passe",
    modify: "Modifier",
    twoFA: "2FA",
    twoFAActive: "2FA Active",
    twoFAInactive: "2FA Desactive",
    twoFAProtected: "Compte protege",
    twoFAActivate: "Activez la 2FA",
    disable: "Desactiver",
    activate: "Activer",
    maintenanceMode: "Mode maintenance",
    maintenanceDesc: "Rendre le site inaccessible aux non-admin",
    // Appearance tab
    localization: "Localisation",
    language: "Langue",
    timezone: "Fuseau horaire",
    currency: "Devise",
    // System tab
    backups: "Sauvegardes",
    autoBackup: "Sauvegarde automatique",
    autoBackupDesc: "Sauvegardes regulieres de la BD",
    frequency: "Frequence",
    daily: "Quotidien",
    weekly: "Hebdomadaire",
    monthly: "Mensuel",
    // Modals
    changePasswordTitle: "Changer le mot de passe",
    currentPassword: "Mot de passe actuel",
    newPassword: "Nouveau mot de passe",
    confirmPassword: "Confirmer",
    passwordHint: "Min 6 caracteres, majuscule, minuscule, chiffre, caractere special",
    cancel: "Annuler",
    change: "Changer",
    schoolModalTitle: "Etablissement",
    newSchoolModal: "Nouvel",
    editSchoolModal: "Modifier",
    name: "Nom",
    status: "Statut",
    director: "Directeur",
    directorEmailLabel: "Email directeur",
    accreditationLabel: "N° agrement",
    taxLabel: "N° fiscal",
    bankLabel: "Banque",
    accountLabel: "N° compte",
    create: "Creer",
    update: "Mettre a jour",
    yearModalTitle: "Annee Scolaire",
    newYearModal: "Nouvelle",
    editYearModal: "Modifier",
    label: "Libelle",
    labelPlaceholder: "Ex: 2025-2026",
    startDateLabel: "Date debut",
    endDateLabel: "Date fin",
    school: "Ecole",
    academicConfig: "Configuration academique",
    setCurrent: "Definir comme annee scolaire active",
    twoFAModalTitle: "Configurer la 2FA",
    twoFAScan: "Scannez ce QR code avec Google Authenticator",
    secretKey: "Cle secrete",
    verifyCode: "Code de verification",
    codePlaceholder: "000000",
    enable2FA: "Activer la 2FA",
    // Audit
    auditSchoolCreated: "Etablissement cree",
    auditSchoolUpdated: "Etablissement mis a jour",
    auditSchoolDeleted: "Etablissement supprime",
    auditYearCreated: "Annee scolaire creee",
    auditYearUpdated: "Annee scolaire mise a jour",
    auditYearDeleted: "Annee scolaire supprimee",
    auditYearSetActive: "Annee scolaire active mise a jour",
    auditSettingsUpdated: "Parametres de l'etablissement mis a jour",
    auditLogoUploaded: "Logo de l'etablissement mis a jour",
    // Errors / Success
    accessDenied: "Acces refuse. Consultation uniquement.",
    noSchoolSelected: "Aucune ecole selectionnee",
    schoolIdRequired: "ID de l'ecole requis",
    saveError: "Erreur lors de la sauvegarde",
    deleteError: "Erreur suppression",
    passwordChanged: "Mot de passe change",
    passwordMismatch: "Les mots de passe ne correspondent pas",
    twoFAEnabled: "2FA active",
    twoFADisabled: "2FA desactive",
    invalidCode: "Code invalide",
    confirmDeleteSchool: "Supprimer cet etablissement ?",
    confirmDeleteYear: "Supprimer cette annee scolaire ?",
    deleteYearWarning: "Cette action est irreversible et supprimera toutes les donnees associees.",
    deleteYearConsequences: "Toutes les donnees associees (classes, matieres, notes, emplois du temps) seront definitivement supprimees.",
    deleteCurrentYearWarning: "Cette annee est actuellement active. Sa suppression desactivera toutes les operations scolaires.",
    confirmDeleteYearCheckbox: "Je comprends que cette action est irreversible.",
    deleting: "Suppression...",
    confirmDelete: "Confirmer la suppression",
    confirmDisable2FA: "Desactiver la 2FA ?",
    selectImage: "Veuillez selectionner une image (PNG, JPG, SVG)",
    imageTooLarge: "L'image ne doit pas depasser 5 Mo",
    logoUpdated: "Logo mis a jour avec succes",
    uploadError: "Erreur lors du telechargement du logo",
    noSchoolForLogo: "Aucune ecole selectionnee. Veuillez d'abord creer une ecole.",
    defaultSchoolLoaded: "Aucune ecole trouvee en base, valeurs par defaut chargees",
    // Academic year duplication
    copyFromPrevious: "Reconduire depuis une annee precedente",
    sourceYear: "Annee scolaire de reference",
    sourceYearHint: "Selectionnez l'annee a partir de laquelle copier les donnees",
    copyTeachers: "Enseignants",
    copySubjects: "Matieres",
    copyClasses: "Classes",
    copyAssignments: "Affectations (enseignants / matieres / classes)",
    generatePeriods: "Generer les periodes pour emploi du temps",
    generateNone: "Ne rien generer",
    generateWeeks: "Generer les semaines",
    generateMonths: "Generer les mois",
    copyFees: "Frais d'inscription et de scolarite",
    copyPrograms: "Programmes de classe",
    copyQuotas: "Quotas horaires",
    copyWeights: "Ponderations (Notes/Moyennes)",
    copyFormulas: "Formules (TH, Moyenne, Rang)",
    overwriteExisting: "Ecraser les parametres existants",
    duplicateYear: "Dupliquer l'annee",
    duplicateYearDesc: "Cree une nouvelle annee en copiant les donnees de l'annee de reference",
    copyElements: "Elements a reproduire",
    copyConfig: "Parametres a reconduire",
    // ✅ NOUVEAU : Classes permanentes et gestion des doublons
    permanentClasses: "Classes permanentes",
    skipPermanentClasses: "Ignorer les classes permanentes",
    skipPermanentClassesHelp: "Les classes permanentes (sans annee scolaire) ne sont pas dupliquees. Elles sont reutilisees automatiquement.",
    duplicateStrategy: "Strategie en cas de doublons",
    duplicateStrategySkip: "Ignorer (ne pas copier si existe deja)",
    duplicateStrategyMerge: "Fusionner (mettre a jour les existants)",
    duplicateStrategyOverwrite: "Ecraser (remplacer les existants)",
    duplicateStrategyHelp: "Choisissez comment gerer les classes et matieres qui existent deja dans l'annee cible.",
    noDuplicateWarning: "Aucun doublon detecte grace aux classes permanentes.",
  },
  en: {
    pageTitle: "School Settings",
    pageSubtitle: "General configuration for Lycee Bilingue de Baleng",
    readonlyBadge: "Read only",
    saveBtn: "Save",
    savingBtn: "Saving...",
    changeLogo: "Change logo",
    tabs: {
      general: 'General',
      academic: 'Academic',
      schools: 'Schools',
      notifications: 'Notifications',
      security: 'Security',
      appearance: 'Appearance',
      system: 'System',
    },
    // General tab
    mySchoolInfo: "You are editing your school information.",
    schoolInfo: "School Information",
    fullName: "Full Name",
    shortName: "Short Name (Acronym)",
    shortNameHint: 'Saves to the "acronym" field in the database',
    foundedYear: "Founded Year",
    address: "Address",
    city: "City",
    country: "Country",
    phone: "Phone",
    email: "Email",
    website: "Website",
    websitePlaceholder: "https://",
    direction: "Management",
    directorName: "Director Name",
    directorEmail: "Director Email",
    legalInfo: "Legal Information",
    accreditationNum: "Accreditation Number",
    taxId: "Tax ID",
    bankInfo: "Bank Information",
    bankName: "Bank",
    accountNum: "Account Number",
    iban: "IBAN",
    // Academic tab
    mySchoolYears: "School Years for {name}",
    mySchoolYearsDesc: "You are viewing your school's academic years.",
    activeYear: "Active School Year",
    academicYears: "Academic Years",
    newYear: "New Year",
    noYears: "No academic years configured",
    configAcademic: "Academic Configuration",
    startDate: "School Year Start",
    endDate: "School Year End",
    termsCount: "Number of Terms",
    sequencesCount: "Sequences per Term",
    semesters: "2 (Semesters)",
    trimesters: "3 (Trimesters)",
    gradingScale: "Grading Scale",
    maxScale: "Maximum Scale",
    passThreshold: "Pass Threshold",
    honorThreshold: "Honor Roll Threshold",
    // Schools tab
    mySchoolBanner: "My School",
    mySchoolBannerDesc: "You are viewing your school information and active academic year.",
    mySchoolBannerAdmin: "You are viewing your school and all its academic years.",
    schoolsTitle: "Schools",
    mySchoolTitle: "My School",
    newSchool: "New School",
    noSchools: "No schools configured",
    noSchoolsUser: "No school associated with your account",
    yearsAllSchools: "Academic Years (All Schools)",
    yearsMySchool: "Academic Years of My School",
    myYear: "My Academic Year",
    noYearsAvailable: "No academic years available",
    active: "Active",
    inactive: "Inactive",
    mySchoolBadge: "My School",
    selected: "Selected",
    setActive: "Set Active",
    // Notifications tab
    preferences: "Preferences",
    emailNotif: "Email Notifications",
    emailNotifDesc: "Send alerts by email",
    smsNotif: "SMS Notifications",
    smsNotifDesc: "Urgent alerts by SMS",
    // Security tab
    password: "Password",
    changePassword: "Change Password",
    changePasswordDesc: "Update your password",
    modify: "Change",
    twoFA: "2FA",
    twoFAActive: "2FA Active",
    twoFAInactive: "2FA Inactive",
    twoFAProtected: "Account protected",
    twoFAActivate: "Enable 2FA",
    disable: "Disable",
    activate: "Enable",
    maintenanceMode: "Maintenance Mode",
    maintenanceDesc: "Make site inaccessible to non-admins",
    // Appearance tab
    localization: "Localization",
    language: "Language",
    timezone: "Timezone",
    currency: "Currency",
    // System tab
    backups: "Backups",
    autoBackup: "Automatic Backup",
    autoBackupDesc: "Regular database backups",
    frequency: "Frequency",
    daily: "Daily",
    weekly: "Weekly",
    monthly: "Monthly",
    // Modals
    changePasswordTitle: "Change Password",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmPassword: "Confirm",
    passwordHint: "Min 6 chars, uppercase, lowercase, number, special char",
    cancel: "Cancel",
    change: "Change",
    schoolModalTitle: "School",
    newSchoolModal: "New",
    editSchoolModal: "Edit",
    name: "Name",
    status: "Status",
    director: "Director",
    directorEmailLabel: "Director Email",
    accreditationLabel: "Accreditation No.",
    taxLabel: "Tax ID",
    bankLabel: "Bank",
    accountLabel: "Account No.",
    create: "Create",
    update: "Update",
    yearModalTitle: "Academic Year",
    newYearModal: "New",
    editYearModal: "Edit",
    label: "Label",
    labelPlaceholder: "Ex: 2025-2026",
    startDateLabel: "Start Date",
    endDateLabel: "End Date",
    school: "School",
    academicConfig: "Academic Configuration",
    setCurrent: "Set as active academic year",
    twoFAModalTitle: "Configure 2FA",
    twoFAScan: "Scan this QR code with Google Authenticator",
    secretKey: "Secret Key",
    verifyCode: "Verification Code",
    codePlaceholder: "000000",
    enable2FA: "Enable 2FA",
    // Audit
    auditSchoolCreated: "School created",
    auditSchoolUpdated: "School updated",
    auditSchoolDeleted: "School deleted",
    auditYearCreated: "Academic year created",
    auditYearUpdated: "Academic year updated",
    auditYearDeleted: "Academic year deleted",
    auditYearSetActive: "Active academic year updated",
    auditSettingsUpdated: "School settings updated",
    auditLogoUploaded: "School logo updated",
    // Errors / Success
    accessDenied: "Access denied. Read only.",
    noSchoolSelected: "No school selected",
    schoolIdRequired: "School ID required",
    saveError: "Error saving",
    deleteError: "Delete error",
    passwordChanged: "Password changed",
    passwordMismatch: "Passwords do not match",
    twoFAEnabled: "2FA enabled",
    twoFADisabled: "2FA disabled",
    invalidCode: "Invalid code",
    confirmDeleteSchool: "Delete this school?",
    confirmDeleteYear: "Delete this academic year?",
    deleteYearWarning: "This action is irreversible and will delete all associated data.",
    deleteYearConsequences: "All associated data (classes, subjects, grades, timetables) will be permanently deleted.",
    deleteCurrentYearWarning: "This year is currently active. Deleting it will disable all school operations.",
    confirmDeleteYearCheckbox: "I understand this action is irreversible.",
    deleting: "Deleting...",
    confirmDelete: "Confirm deletion",
    confirmDisable2FA: "Disable 2FA?",
    selectImage: "Please select an image (PNG, JPG, SVG)",
    imageTooLarge: "Image must not exceed 5 MB",
    logoUpdated: "Logo updated successfully",
    uploadError: "Error uploading logo",
    noSchoolForLogo: "No school selected. Please create a school first.",
    defaultSchoolLoaded: "No school found in database, default values loaded",
    // Academic year duplication
    copyFromPrevious: "Copy from previous year",
    sourceYear: "Reference academic year",
    sourceYearHint: "Select the year to copy data from",
    copyTeachers: "Teachers",
    copySubjects: "Subjects",
    copyClasses: "Classes",
    copyAssignments: "Assignments (teachers / subjects / classes)",
    generatePeriods: "Generate timetable periods",
    generateNone: "Do not generate",
    generateWeeks: "Generate weeks",
    generateMonths: "Generate months",
    copyFees: "Registration and tuition fees",
    copyPrograms: "Class programs",
    copyQuotas: "Hourly quotas",
    copyWeights: "Weightings (Grades/Averages)",
    copyFormulas: "Formulas (Avg, Rank, Honor)",
    overwriteExisting: "Overwrite existing parameters",
    duplicateYear: "Duplicate year",
    duplicateYearDesc: "Create a new year by copying data from the reference year",
    copyElements: "Elements to reproduce",
    copyConfig: "Parameters to carry over",
    // ✅ NEW: Permanent classes and duplicate handling
    permanentClasses: "Permanent Classes",
    skipPermanentClasses: "Skip permanent classes",
    skipPermanentClassesHelp: "Permanent classes (without academic year) are not duplicated. They are automatically reused.",
    duplicateStrategy: "Duplicate handling strategy",
    duplicateStrategySkip: "Skip (do not copy if already exists)",
    duplicateStrategyMerge: "Merge (update existing ones)",
    duplicateStrategyOverwrite: "Overwrite (replace existing ones)",
    duplicateStrategyHelp: "Choose how to handle classes and subjects that already exist in the target year.",
    noDuplicateWarning: "No duplicates detected thanks to permanent classes.",
  }
}

type Lang = 'fr' | 'en'

// ==================== TYPES ====================
interface SchoolSettings {
  id: number
  name: string
  short_name: string
  acronym?: string
  address: string
  city: string
  country: string
  phone: string
  email: string
  website: string
  logo_url?: string
  founded_year: number
  director_name: string
  director_email: string
  accreditation_number: string
  tax_id: string
  bank_name: string
  bank_account: string
  bank_iban: string
  currency: string
  language: string
  timezone: string
  academic_year_start: string
  academic_year_end: string
  terms_count: number
  sequences_per_term: number
  grading_scale: number
  pass_threshold: number
  honor_threshold: number
  email_notifications: boolean
  sms_notifications: boolean
  auto_backup: boolean
  backup_frequency: string
  maintenance_mode: boolean
}

interface AcademicYear {
  id: number
  label: string
  start_date: string
  end_date: string
  is_current: boolean
  school_id: number
  is_selected?: boolean
  grading_scale?: number
  pass_threshold?: number
  honor_threshold?: number
  terms_count?: number
  sequences_per_term?: number
}

interface School {
  id: number
  name: string
  short_name: string
  acronym?: string
  address?: string
  city?: string
  country?: string
  phone?: string
  email?: string
  website?: string
  logo_url?: string
  founded_year?: number
  director_name?: string
  director_email?: string
  accreditation_number?: string
  tax_id?: string
  bank_name?: string
  bank_account?: string
  bank_iban?: string
  status: string
  created_at?: string
}

interface AuditLogPayload {
  action: string
  entity_type: string
  entity_id?: number
  details?: string
  school_id?: number
}

const CURRENCIES = [
  { code: 'XAF', label: 'FCFA (XAF)' },
  { code: 'EUR', label: 'Euro (EUR)' },
  { code: 'USD', label: 'Dollar US (USD)' },
]

const LANGUAGES = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'fr-en', label: 'Bilingual FR/EN' },
]

const TIMEZONES = [
  { code: 'Africa/Douala', label: 'Douala (WAT)' },
  { code: 'Africa/Lagos', label: 'Lagos (WAT)' },
  { code: 'UTC', label: 'UTC' },
]

const BACKUP_FREQUENCIES = [
  { code: 'daily', label_fr: 'Quotidien', label_en: 'Daily' },
  { code: 'weekly', label_fr: 'Hebdomadaire', label_en: 'Weekly' },
  { code: 'monthly', label_fr: 'Mensuel', label_en: 'Monthly' },
]

const TABS = [
  { id: 'general', icon: School },
  { id: 'academic', icon: AcademicIcon },
  { id: 'schools', icon: Building2 },
  { id: 'notifications', icon: Bell },
  { id: 'security', icon: Shield },
  { id: 'appearance', icon: Palette },
  { id: 'system', icon: Database },
]

const FULL_ACCESS_ROLES = ['super_admin', 'admin', 'proviseur']

function validatePassword(password: string): { valid: boolean; message: string } {
  if (password.length < 6) {
    return { valid: false, message: 'Min 6 chars' }
  }
  if (!/[a-z]/.test(password)) {
    return { valid: false, message: 'Need lowercase' }
  }
  if (!/[A-Z]/.test(password)) {
    return { valid: false, message: 'Need uppercase' }
  }
  if (!/\d/.test(password)) {
    return { valid: false, message: 'Need number' }
  }
  if (!/[@$!%*?&+#-]/.test(password)) {
    return { valid: false, message: 'Need special char' }
  }
  return { valid: true, message: '' }
}

function getLogoUrl(logoPath?: string): string {
  if (!logoPath) return ''
  if (logoPath.startsWith('http')) return logoPath
  const baseUrl = 'http://127.0.0.1:8001'
  return `${baseUrl}${logoPath.startsWith('/') ? logoPath : `/${logoPath}`}`
}


// Helper to normalize school data from backend (acronym -> short_name)
function normalizeSchool(school: any): School {
  return {
    ...school,
    short_name: school.short_name || school.acronym || '',
  } as School
}

export default function SettingsPage() {
  // Language state - shared with other components via localStorage
  const [lang, setLang] = useState<Lang>(() => {
    const stored = localStorage.getItem('app_language') as Lang
    return stored === 'en' ? 'en' : 'fr'
  })

  const t = useMemo(() => TRANSLATIONS[lang], [lang])

  const toggleLanguage = useCallback(() => {
    const newLang = lang === 'fr' ? 'en' : 'fr'
    setLang(newLang)
    localStorage.setItem('app_language', newLang)
    // Broadcast to other tabs/components
    window.dispatchEvent(new StorageEvent('storage', { key: 'app_language', newValue: newLang }))
  }, [lang])

  // Listen for language changes from other components
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'app_language' && (e.newValue === 'fr' || e.newValue === 'en')) {
        setLang(e.newValue as Lang)
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  const { user, roleName, isAdmin, isSuperAdmin } = useAuthStore()

  const canModify = FULL_ACCESS_ROLES.includes(roleName || '') || isAdmin || isSuperAdmin
  const isReadonly = !canModify

  const userSchoolId = user?.school_id ?? null
  const isSuperAdminRole = roleName === 'super_admin' || isSuperAdmin
  const isAdminRole = roleName === 'admin' || isAdmin
  const isProviseurRole = roleName === 'proviseur'

  const [activeTab, setActiveTab] = useState('general')
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState<SchoolSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [formData, setFormData] = useState<Partial<SchoolSettings>>({})
  const [schoolId, setSchoolId] = useState<number | null>(null)
  const [currentAcademicYear, setCurrentAcademicYear] = useState<AcademicYear | null>(null)
  const [schools, setSchools] = useState<School[]>([])
  const [schoolsLoading, setSchoolsLoading] = useState(false)
  const [showSchoolModal, setShowSchoolModal] = useState(false)
  const [editingSchool, setEditingSchool] = useState<School | null>(null)
  const [schoolFormData, setSchoolFormData] = useState<Partial<School>>({
    name: '', short_name: '', address: '', city: '', country: 'Cameroun',
    phone: '', email: '', website: '', founded_year: new Date().getFullYear(),
    director_name: '', director_email: '', accreditation_number: '', tax_id: '',
    bank_name: '', bank_account: '', bank_iban: '', status: 'active'
  })
  const [allAcademicYears, setAllAcademicYears] = useState<AcademicYear[]>([])
  const [academicYearsLoading, setAcademicYearsLoading] = useState(false)
  const [showAcademicYearModal, setShowAcademicYearModal] = useState(false)
  const [showDeleteYearModal, setShowDeleteYearModal] = useState(false)
  const [deleteYearTarget, setDeleteYearTarget] = useState<AcademicYear | null>(null)
  const [isDeletingYear, setIsDeletingYear] = useState(false)
  const [editingAcademicYear, setEditingAcademicYear] = useState<AcademicYear | null>(null)
  const [academicYearFormData, setAcademicYearFormData] = useState({
    label: '', start_date: '', end_date: '', is_current: false, school_id: 0,
    grading_scale: 20, pass_threshold: 10, honor_threshold: 12, terms_count: 3, sequences_per_term: 2,
    // Duplication options
    source_year_id: null as number | null,
    copy_teachers: false,
    copy_subjects: false,
    copy_classes: false,
    copy_assignments: false,
    generate_periods: 'none' as 'none' | 'weeks' | 'months',
    copy_fees: false,
    overwrite_fees: false,
    copy_programs: false,
    overwrite_programs: false,
    copy_quotas: false,
    overwrite_quotas: false,
    copy_weights: false,
    overwrite_weights: false,
    copy_formulas: false,
    overwrite_formulas: false,
    // ✅ NOUVEAU : Gestion des classes permanentes et doublons
    skip_permanent_classes: true,
    duplicate_strategy: 'skip' as 'skip' | 'merge' | 'overwrite',
  })
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [passwordData, setPasswordData] = useState({
    current_password: '', new_password: '', confirm_password: '',
  })
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [twoFAStatus, setTwoFAStatus] = useState<{ enabled: boolean; method?: string } | null>(null)
  const [show2FAModal, setShow2FAModal] = useState(false)
  const [twoFASetup, setTwoFASetup] = useState<{ secret: string; qr_code: string; uri: string } | null>(null)
  const [twoFACode, setTwoFACode] = useState('')

  // Helper to log audit actions
  const logAudit = useCallback(async (payload: AuditLogPayload) => {
    try {
      // Try to use adminApi if available, otherwise skip silently
      if (adminApi && typeof (adminApi as any).logAudit === 'function') {
        await (adminApi as any).logAudit(payload)
      }
    } catch (err) {
      console.warn('Audit log failed:', err)
    }
  }, [])

  const visibleSchools = useMemo(() => {
    if (isSuperAdminRole) return schools
    if (userSchoolId) return schools.filter(s => s.id === userSchoolId)
    return []
  }, [schools, isSuperAdminRole, userSchoolId])

  const visibleAcademicYears = useMemo(() => {
    if (isSuperAdminRole) return allAcademicYears
    if (isAdminRole || isProviseurRole) {
      return userSchoolId ? allAcademicYears.filter(y => y.school_id === userSchoolId) : []
    }
    if (userSchoolId && currentAcademicYear) return [currentAcademicYear]
    return []
  }, [allAcademicYears, isSuperAdminRole, isAdminRole, isProviseurRole, userSchoolId, currentAcademicYear])

  const loadSettings = useCallback(async () => {
    setLoading(true)
    try {
      const schoolsRes = await schoolApi.getSchools()
      const schoolsList = Array.isArray(schoolsRes) ? schoolsRes : schoolsRes?.data || []
      let school: School | undefined
      if (userSchoolId) {
        school = normalizeSchool(schoolsList.find((s: any) => s.id === userSchoolId))
      }
      if (!school && schoolsList.length > 0) school = schoolsList[0]

      if (school) {
        setSchoolId(school.id)
        const mappedSettings: Partial<SchoolSettings> = {
          id: school.id,
          name: school.name || 'Lycee Bilingue de Baleng',
          short_name: school.short_name || school.acronym || 'LYBIBAL',
          address: school.address || '', city: school.city || 'Baleng', country: school.country || 'Cameroun',
          phone: school.phone || '', email: school.email || '', website: school.website || '',
          logo_url: school.logo_url,
          founded_year: school.founded_year || new Date().getFullYear(),
          director_name: school.director_name || '', director_email: school.director_email || '',
          accreditation_number: school.accreditation_number || '', tax_id: school.tax_id || '',
          bank_name: school.bank_name || '', bank_account: school.bank_account || '', bank_iban: school.bank_iban || '',
          currency: 'XAF', language: 'fr-en', timezone: 'Africa/Douala',
          academic_year_start: `${new Date().getFullYear()}-09-01`,
          academic_year_end: `${new Date().getFullYear() + 1}-06-30`,
          terms_count: 3, sequences_per_term: 2,
          grading_scale: 20, pass_threshold: 10, honor_threshold: 12,
          email_notifications: true, sms_notifications: false,
          auto_backup: true, backup_frequency: 'weekly', maintenance_mode: false,
        }
        setSettings(mappedSettings as SchoolSettings)
        setFormData(mappedSettings)
        await loadCurrentAcademicYear(school.id)
      } else {
        throw new Error('No school found')
      }
    } catch (err) {
      console.error('Error loading settings:', err)
      const defaultSettings: Partial<SchoolSettings> = {
        name: 'Lycee Bilingue de Baleng', short_name: 'LYBIBAL',
        address: '', city: 'Baleng', country: 'Cameroun',
        phone: '', email: '', website: '',
        founded_year: new Date().getFullYear(),
        director_name: '', director_email: '',
        accreditation_number: '', tax_id: '',
        bank_name: '', bank_account: '', bank_iban: '',
        currency: 'XAF', language: 'fr-en', timezone: 'Africa/Douala',
        academic_year_start: `${new Date().getFullYear()}-09-01`,
        academic_year_end: `${new Date().getFullYear() + 1}-06-30`,
        terms_count: 3, sequences_per_term: 2,
        grading_scale: 20, pass_threshold: 10, honor_threshold: 12,
        email_notifications: true, sms_notifications: false,
        auto_backup: true, backup_frequency: 'weekly', maintenance_mode: false,
      }
      setSettings(defaultSettings as SchoolSettings)
      setFormData(defaultSettings)
      toast.warning(t.defaultSchoolLoaded)
    } finally {
      setLoading(false)
    }
  }, [userSchoolId, t.defaultSchoolLoaded])

  const loadCurrentAcademicYear = useCallback(async (sid: number) => {
    try {
      let allYears: AcademicYear[] = []
      try {
        const res = await schoolApi.getAcademicYears(sid)
        const rawYears = Array.isArray(res) ? res : res?.data || []
        // Normalize years data
        allYears = rawYears.map((y: any) => ({...y, label: y.label || y.name || ''}))
      } catch (err: any) {
        console.warn('Error loading academic years for school:', sid, err)
      }
      let selectedYear: AcademicYear | null = null
      if (allYears.length > 0) {
        selectedYear = allYears.find((y: AcademicYear) => y.is_current) || null
        if (!selectedYear) selectedYear = allYears.find((y: AcademicYear) => y.is_selected) || null
        if (!selectedYear) selectedYear = allYears[0]
      }
      if (selectedYear) {
        setCurrentAcademicYear(selectedYear)
        setFormData(prev => ({
          ...prev,
          academic_year_start: selectedYear.start_date || prev.academic_year_start,
          academic_year_end: selectedYear.end_date || prev.academic_year_end,
          terms_count: selectedYear.terms_count || prev.terms_count,
          sequences_per_term: selectedYear.sequences_per_term || prev.sequences_per_term,
          grading_scale: selectedYear.grading_scale || prev.grading_scale,
          pass_threshold: selectedYear.pass_threshold ? Number(selectedYear.pass_threshold) : prev.pass_threshold,
          honor_threshold: selectedYear.honor_threshold ? Number(selectedYear.honor_threshold) : prev.honor_threshold,
        }))
      } else {
        console.warn(`No academic year found for school ${sid}`)
        setCurrentAcademicYear(null)
      }
    } catch (err: any) {
      console.warn('Error loading current academic year:', err)
      setCurrentAcademicYear(null)
    }
  }, [])

  const loadSchools = useCallback(async () => {
    setSchoolsLoading(true)
    try {
      const res = await schoolApi.getSchools()
      const rawSchools = Array.isArray(res) ? res : res?.data || []
      // Normalize: backend may return "acronym" instead of "short_name"
      setSchools(rawSchools.map((s: any) => normalizeSchool(s)))
    } catch (err) {
      console.error('Error loading schools:', err)
      setSchools([])
    } finally {
      setSchoolsLoading(false)
    }
  }, [])

  const loadAllAcademicYears = useCallback(async () => {
    setAcademicYearsLoading(true)
    try {
      if (isSuperAdminRole) {
        const allYears: AcademicYear[] = []
        for (const school of schools) {
          try {
            const res = await schoolApi.getAcademicYears(school.id)
            const years = Array.isArray(res) ? res : res?.data || []
            allYears.push(...years)
          } catch (err) {
            console.warn(`Error loading years for school ${school.id}:`, err)
          }
        }
        setAllAcademicYears(allYears)
      } else if (userSchoolId) {
        const res = await schoolApi.getAcademicYears(userSchoolId)
        setAllAcademicYears(Array.isArray(res) ? res : res?.data || [])
      } else {
        setAllAcademicYears([])
      }
    } catch (err) {
      console.error('Error loading all academic years:', err)
      setAllAcademicYears([])
    } finally {
      setAcademicYearsLoading(false)
    }
  }, [isSuperAdminRole, userSchoolId, schools])

  const loadAcademicYears = useCallback(async () => {
    setAcademicYearsLoading(true)
    try {
      if (!schoolId) return
      const res = await schoolApi.getAcademicYears(schoolId)
      const years = Array.isArray(res) ? res : res?.data || []
      if (!isSuperAdminRole && userSchoolId === schoolId) setAllAcademicYears(years)
    } catch (err) {
      console.error('Error loading academic years:', err)
    } finally {
      setAcademicYearsLoading(false)
    }
  }, [schoolId, isSuperAdminRole, userSchoolId])

  const load2FAStatus = useCallback(async () => {
    try {
      const res = await authApi.get2FAStatus()
      setTwoFAStatus(res)
    } catch (err) {
      console.error('2FA status error', err)
    }
  }, [])

  useEffect(() => { loadSettings() }, [loadSettings])
  useEffect(() => { loadSchools() }, [loadSchools])
  useEffect(() => { if (schoolId) loadAcademicYears() }, [schoolId, loadAcademicYears])
  useEffect(() => { 
    if (activeTab === 'schools' || activeTab === 'academic') loadAllAcademicYears()
  }, [activeTab, loadAllAcademicYears])
  useEffect(() => { if (activeTab === 'security') load2FAStatus() }, [activeTab, load2FAStatus])

  const handleSaveSchool = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isReadonly) { toast.error(t.accessDenied); return }
    try {
      if (editingSchool) {
        await schoolApi.updateSchool(editingSchool.id, schoolFormData)
        toast.success(t.auditSchoolUpdated)
        await logAudit({
          action: 'UPDATE',
          entity_type: 'school',
          entity_id: editingSchool.id,
          details: `Updated school: ${schoolFormData.name || editingSchool.name}`,
          school_id: editingSchool.id,
        })
      } else {
        const createdRes = await schoolApi.createSchool(schoolFormData)
        const createdSchool = normalizeSchool(createdRes)
        toast.success(t.auditSchoolCreated)
        await logAudit({
          action: 'CREATE',
          entity_type: 'school',
          entity_id: createdSchool?.id || createdRes?.id,
          details: `Created school: ${schoolFormData.name}`,
          school_id: createdSchool?.id || createdRes?.id,
        })
      }
      setShowSchoolModal(false)
      setEditingSchool(null)
      loadSchools()
      loadSettings()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t.saveError)
    }
  }

  const handleDeleteSchool = async (id: number) => {
    if (isReadonly) { toast.error(t.accessDenied); return }
    if (!confirm(t.confirmDeleteSchool)) return
    try {
      await schoolApi.deleteSchool(id)
      toast.success(t.auditSchoolDeleted)
      await logAudit({
        action: 'DELETE',
        entity_type: 'school',
        entity_id: id,
        details: `Deleted school ID: ${id}`,
        school_id: id,
      })
      loadSchools()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t.deleteError)
    }
  }

  const openEditSchool = (s: School) => {
    if (isReadonly) { toast.error(t.accessDenied); return }
    setEditingSchool(s)
    setSchoolFormData({
      name: s.name || '', short_name: s.short_name || '',
      address: s.address || '', city: s.city || '', country: s.country || '',
      phone: s.phone || '', email: s.email || '', website: s.website || '',
      founded_year: s.founded_year || new Date().getFullYear(),
      director_name: s.director_name || '', director_email: s.director_email || '',
      accreditation_number: s.accreditation_number || '', tax_id: s.tax_id || '',
      bank_name: s.bank_name || '', bank_account: s.bank_account || '',
      bank_iban: s.bank_iban || '', status: s.status || 'active', logo_url: s.logo_url
    })
    setShowSchoolModal(true)
  }

  const openCreateSchool = () => {
    if (isReadonly) { toast.error(t.accessDenied); return }
    setEditingSchool(null)
    setSchoolFormData({
      name: '', short_name: '', address: '', city: '', country: 'Cameroun',
      phone: '', email: '', website: '', founded_year: new Date().getFullYear(),
      director_name: '', director_email: '', accreditation_number: '', tax_id: '',
      bank_name: '', bank_account: '', bank_iban: '', status: 'active'
    })
    setShowSchoolModal(true)
  }

  const handleSaveAcademicYear = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isReadonly) { toast.error(t.accessDenied); return }
    try {
      const payload = { ...academicYearFormData, school_id: academicYearFormData.school_id || schoolId }
      if (!payload.school_id) { toast.error(t.schoolIdRequired); return }

      // If duplicating from a source year, use the duplicate endpoint
      if (!editingAcademicYear && payload.source_year_id) {
        const duplicatePayload = {
          label: payload.label,
          start_date: payload.start_date,
          end_date: payload.end_date,
          is_current: payload.is_current,
          source_year_id: payload.source_year_id,
          copy_teachers: payload.copy_teachers,
          copy_subjects: payload.copy_subjects,
          copy_classes: payload.copy_classes,
          copy_assignments: payload.copy_assignments,
          generate_periods: payload.generate_periods,
          copy_fees: payload.copy_fees,
          overwrite_fees: payload.overwrite_fees,
          copy_programs: payload.copy_programs,
          overwrite_programs: payload.overwrite_programs,
          copy_quotas: payload.copy_quotas,
          overwrite_quotas: payload.overwrite_quotas,
          copy_weights: payload.copy_weights,
          overwrite_weights: payload.overwrite_weights,
          copy_formulas: payload.copy_formulas,
          overwrite_formulas: payload.overwrite_formulas,
          grading_scale: payload.grading_scale,
          pass_threshold: payload.pass_threshold,
          honor_threshold: payload.honor_threshold,
          terms_count: payload.terms_count,
          sequences_per_term: payload.sequences_per_term,
          // ✅ NOUVEAU : Gestion classes permanentes et doublons
          skip_permanent_classes: payload.skip_permanent_classes,
          duplicate_strategy: payload.duplicate_strategy,
        }
        const res = await schoolApi.duplicateAcademicYear(payload.school_id, duplicatePayload)
        toast.success(t.auditYearCreated)
        await logAudit({
          action: 'CREATE',
          entity_type: 'academic_year',
          entity_id: res?.id,
          details: `Duplicated academic year from ${payload.source_year_id}: ${payload.label}`,
          school_id: payload.school_id,
        })
      } else if (editingAcademicYear) {
        await schoolApi.updateAcademicYear(payload.school_id, editingAcademicYear.id, payload)
        toast.success(t.auditYearUpdated)
        await logAudit({
          action: 'UPDATE',
          entity_type: 'academic_year',
          entity_id: editingAcademicYear.id,
          details: `Updated academic year: ${payload.label}`,
          school_id: payload.school_id,
        })
      } else {
        const res = await schoolApi.createAcademicYear(payload.school_id, payload)
        toast.success(t.auditYearCreated)
        await logAudit({
          action: 'CREATE',
          entity_type: 'academic_year',
          entity_id: res?.id,
          details: `Created academic year: ${payload.label}`,
          school_id: payload.school_id,
        })
      }
      setShowAcademicYearModal(false)
      setEditingAcademicYear(null)
      loadAcademicYears()
      loadAllAcademicYears()
      if (schoolId) loadCurrentAcademicYear(schoolId)
    } catch (err: any) {
      console.error('Error saving academic year:', err)
      toast.error(err?.response?.data?.detail || err?.message || t.saveError)
    }
  }

  const openDeleteYearModal = (year: AcademicYear) => {
    if (isReadonly) { toast.error(t.accessDenied); return }
    setDeleteYearTarget(year)
    setShowDeleteYearModal(true)
  }

  const closeDeleteYearModal = () => {
    setShowDeleteYearModal(false)
    setDeleteYearTarget(null)
    setIsDeletingYear(false)
  }

  const handleConfirmDeleteYear = async () => {
    if (!deleteYearTarget || !schoolId) { toast.error(t.schoolIdRequired); return }
    setIsDeletingYear(true)
    try {
      await schoolApi.deleteAcademicYear(schoolId, deleteYearTarget.id)
      toast.success(t.auditYearDeleted)
      await logAudit({
        action: 'DELETE',
        entity_type: 'academic_year',
        entity_id: deleteYearTarget.id,
        details: `Deleted academic year ID: ${deleteYearTarget.id} — ${deleteYearTarget.label}`,
        school_id: schoolId,
      })
      closeDeleteYearModal()
      loadAcademicYears()
      loadAllAcademicYears()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t.deleteError)
    } finally {
      setIsDeletingYear(false)
    }
  }

  // Gardé pour compatibilité — remplacé par openDeleteYearModal
  const handleDeleteAcademicYear = async (id: number) => {
    if (isReadonly) { toast.error(t.accessDenied); return }
    const year = allAcademicYears.find(y => y.id === id)
    if (year) openDeleteYearModal(year)
  }

  const handleSetCurrentAcademicYear = async (id: number) => {
    if (isReadonly) { toast.error(t.accessDenied); return }
    try {
      if (!schoolId) { toast.error(t.schoolIdRequired); return }
      await schoolApi.setCurrentAcademicYear(schoolId, id)
      toast.success(t.auditYearSetActive)
      await logAudit({
        action: 'UPDATE',
        entity_type: 'academic_year',
        entity_id: id,
        details: `Set academic year ID ${id} as active`,
        school_id: schoolId,
      })
      loadAcademicYears()
      loadAllAcademicYears()
      loadCurrentAcademicYear(schoolId)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Error')
    }
  }

  const openEditAcademicYear = (y: AcademicYear) => {
    if (isReadonly) { toast.error(t.accessDenied); return }
    setEditingAcademicYear(y)
    setAcademicYearFormData({
      label: y.label, start_date: y.start_date, end_date: y.end_date,
      is_current: y.is_current, school_id: y.school_id,
      grading_scale: y.grading_scale || 20,
      pass_threshold: y.pass_threshold || 10,
      honor_threshold: y.honor_threshold || 12,
      terms_count: y.terms_count || 3,
      sequences_per_term: y.sequences_per_term || 2,
      source_year_id: null,
      copy_teachers: false, copy_subjects: false, copy_classes: false, copy_assignments: false,
      generate_periods: 'none',
      copy_fees: false, overwrite_fees: false,
      copy_programs: false, overwrite_programs: false,
      copy_quotas: false, overwrite_quotas: false,
      copy_weights: false, overwrite_weights: false,
      copy_formulas: false, overwrite_formulas: false,
      skip_permanent_classes: true,
      duplicate_strategy: 'skip',
    })
    setShowAcademicYearModal(true)
  }

  const openCreateAcademicYear = () => {
    if (isReadonly) { toast.error(t.accessDenied); return }
    setEditingAcademicYear(null)
    setAcademicYearFormData({
      label: '', start_date: '', end_date: '', is_current: false,
      school_id: schoolId || 0,
      grading_scale: 20, pass_threshold: 10, honor_threshold: 12,
      terms_count: 3, sequences_per_term: 2,
      source_year_id: null,
      copy_teachers: false, copy_subjects: false, copy_classes: false, copy_assignments: false,
      generate_periods: 'none',
      copy_fees: false, overwrite_fees: false,
      copy_programs: false, overwrite_programs: false,
      copy_quotas: false, overwrite_quotas: false,
      copy_weights: false, overwrite_weights: false,
      copy_formulas: false, overwrite_formulas: false,
      skip_permanent_classes: true,
      duplicate_strategy: 'skip',
    })
    setShowAcademicYearModal(true)
  }

  const handleSave = async () => {
    if (isReadonly) { toast.error(t.accessDenied); return }
    if (!schoolId) { toast.error(t.noSchoolSelected); return }
    setSaving(true)
    try {
      const updatePayload = {
        name: formData.name,
        acronym: formData.short_name,
        address: formData.address, city: formData.city, country: formData.country,
        phone: formData.phone, email: formData.email, website: formData.website,
        founded_year: formData.founded_year,
        director_name: formData.director_name, director_email: formData.director_email,
        accreditation_number: formData.accreditation_number, tax_id: formData.tax_id,
        bank_name: formData.bank_name, bank_account: formData.bank_account, bank_iban: formData.bank_iban,
      }
      await schoolApi.updateSchool(schoolId, updatePayload)
      await logAudit({
        action: 'UPDATE',
        entity_type: 'school_settings',
        entity_id: schoolId,
        details: `Updated school settings for ID: ${schoolId}`,
        school_id: schoolId,
      })

      if (currentAcademicYear) {
        await schoolApi.updateAcademicYear(schoolId, currentAcademicYear.id, {
          start_date: formData.academic_year_start,
          end_date: formData.academic_year_end,
          terms_count: formData.terms_count,
          sequences_per_term: formData.sequences_per_term,
          grading_scale: formData.grading_scale,
          pass_threshold: formData.pass_threshold,
          honor_threshold: formData.honor_threshold,
        })
        await logAudit({
          action: 'UPDATE',
          entity_type: 'academic_year',
          entity_id: currentAcademicYear.id,
          details: `Updated academic year config for ID: ${currentAcademicYear.id}`,
          school_id: schoolId,
        })
      }

      toast.success(t.auditSettingsUpdated)
      loadSettings()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t.saveError)
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    const validation = validatePassword(passwordData.new_password)
    if (!validation.valid) { setPasswordError(validation.message); return }
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError(t.passwordMismatch)
      return
    }
    try {
      await authApi.changePassword(passwordData.current_password, passwordData.new_password)
      toast.success(t.passwordChanged)
      setShowPasswordModal(false)
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' })
      setPasswordError('')
      await logAudit({
        action: 'UPDATE',
        entity_type: 'user_password',
        details: 'User changed their password',
      })
    } catch (err: any) {
      setPasswordError(err?.response?.data?.detail || 'Error')
    }
  }

  const handleSetup2FA = async () => {
    try {
      const res = await authApi.setup2FA()
      setTwoFASetup(res)
      setShow2FAModal(true)
    } catch (err) {
      toast.error('2FA setup error')
    }
  }

  const handleEnable2FA = async () => {
    try {
      await authApi.enable2FA({ code: twoFACode })
      toast.success(t.twoFAEnabled)
      setShow2FAModal(false)
      setTwoFACode('')
      load2FAStatus()
      await logAudit({
        action: 'ENABLE',
        entity_type: 'two_fa',
        details: 'User enabled 2FA',
      })
    } catch (err) {
      toast.error(t.invalidCode)
    }
  }

  const handleDisable2FA = async () => {
    if (!confirm(t.confirmDisable2FA)) return
    try {
      await authApi.disable2FA({ password: passwordData.current_password })
      toast.success(t.twoFADisabled)
      load2FAStatus()
      await logAudit({
        action: 'DISABLE',
        entity_type: 'two_fa',
        details: 'User disabled 2FA',
      })
    } catch (err) {
      toast.error('Error')
    }
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadonly) { toast.error(t.accessDenied); return }
    const file = e.target.files?.[0]
    if (!file) return
    const currentSchoolId = schoolId || settings?.id || schools[0]?.id
    if (!currentSchoolId) {
      toast.error(t.noSchoolForLogo)
      return
    }
    if (!file.type.startsWith('image/')) {
      toast.error(t.selectImage)
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t.imageTooLarge)
      return
    }
    try {
      setSaving(true)
      const result = await schoolApi.uploadLogo(file, currentSchoolId)
      const logoUrl = result.full_url || result.logo_url
      toast.success(t.logoUpdated)
      setSettings(prev => prev ? { ...prev, logo_url: logoUrl } : null)
      setFormData(prev => ({ ...prev, logo_url: logoUrl }))
      await logAudit({
        action: 'UPDATE',
        entity_type: 'school_logo',
        entity_id: currentSchoolId,
        details: `Updated logo for school ID: ${currentSchoolId}`,
        school_id: currentSchoolId,
      })
      loadSettings()
    } catch (err: any) {
      console.error('Logo upload error:', err)
      toast.error(err?.response?.data?.detail || err?.message || t.uploadError)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  const logoDisplayUrl = getLogoUrl(settings?.logo_url)

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-3">
              <Settings className="h-7 w-7 text-primary-600" />
              {t.pageTitle}
            </h1>
            <p className="page-subtitle">{t.pageSubtitle}</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors flex items-center gap-2"
              title={lang === 'fr' ? 'Switch to English' : 'Passer en Français'}
            >
              <Globe className="w-4 h-4" />
              {lang === 'fr' ? 'FR' : 'EN'}
            </button>
            {isReadonly && (
              <span className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-medium border border-amber-200 flex items-center gap-1">
                <Eye className="w-3 h-3" /> {t.readonlyBadge}
              </span>
            )}
            {activeTab !== 'schools' && canModify && (
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? (
                  <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />{t.savingBtn}</>
                ) : (
                  <><Save className="mr-2 h-4 w-4" />{t.saveBtn}</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <div className="card">
            <div className="card-body p-2">
              <nav className="space-y-1">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {(t.tabs as any)[tab.id]}
                    <ChevronRight className={`w-4 h-4 ml-auto ${activeTab === tab.id ? 'text-primary-500' : 'text-gray-400'}`} />
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <div className="card mt-4">
            <div className="card-body text-center">
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3 overflow-hidden">
                {logoDisplayUrl ? (
                  <img 
                    src={logoDisplayUrl} 
                    alt="Logo" 
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                ) : (
                  <School className="w-10 h-10 text-primary-600" />
                )}
              </div>
              <h3 className="font-semibold text-gray-900">{settings?.name}</h3>
              <p className="text-sm text-gray-500">{settings?.short_name || settings?.acronym}</p>
              {canModify && (
                <label className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-600 rounded-lg text-xs font-medium cursor-pointer hover:bg-primary-100">
                  <Upload className="w-3 h-3" />
                  {t.changeLogo}
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">

          {/* ====== GENERAL TAB ====== */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                <School className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-800">{settings?.name || t.mySchoolTitle}</p>
                  <p className="text-sm text-blue-600">{t.mySchoolInfo}</p>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="font-semibold flex items-center gap-2">
                    <School className="w-5 h-5 text-primary-600" />
                    {t.schoolInfo}
                  </h3>
                </div>
                <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="label">{t.fullName} *</label>
                    <input value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                  <div>
                    <label className="label">{t.shortName} *</label>
                    <input value={formData.short_name || ''} onChange={(e) => setFormData({ ...formData, short_name: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} placeholder="Ex: LYBIBAL" />
                    <p className="text-xs text-gray-400 mt-1">{t.shortNameHint}</p>
                  </div>
                  <div>
                    <label className="label">{t.foundedYear}</label>
                    <input type="number" value={formData.founded_year || ''} onChange={(e) => setFormData({ ...formData, founded_year: parseInt(e.target.value) })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label">{t.address}</label>
                    <input value={formData.address || ''} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                  <div>
                    <label className="label">{t.city}</label>
                    <input value={formData.city || ''} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                  <div>
                    <label className="label">{t.country}</label>
                    <input value={formData.country || ''} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                  <div>
                    <label className="label">{t.phone}</label>
                    <input value={formData.phone || ''} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                  <div>
                    <label className="label">{t.email}</label>
                    <input type="email" value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                  <div>
                    <label className="label">{t.website}</label>
                    <input value={formData.website || ''} onChange={(e) => setFormData({ ...formData, website: e.target.value })} className="input" placeholder={t.websitePlaceholder} disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="font-semibold flex items-center gap-2"><User className="w-5 h-5 text-primary-600" />{t.direction}</h3>
                </div>
                <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">{t.directorName}</label>
                    <input value={formData.director_name || ''} onChange={(e) => setFormData({ ...formData, director_name: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                  <div>
                    <label className="label">{t.directorEmail}</label>
                    <input type="email" value={formData.director_email || ''} onChange={(e) => setFormData({ ...formData, director_email: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="font-semibold flex items-center gap-2"><Globe className="w-5 h-5 text-primary-600" />{t.legalInfo}</h3>
                </div>
                <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">{t.accreditationNum}</label>
                    <input value={formData.accreditation_number || ''} onChange={(e) => setFormData({ ...formData, accreditation_number: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                  <div>
                    <label className="label">{t.taxId}</label>
                    <input value={formData.tax_id || ''} onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="font-semibold flex items-center gap-2"><Database className="w-5 h-5 text-primary-600" />{t.bankInfo}</h3>
                </div>
                <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">{t.bankName}</label>
                    <input value={formData.bank_name || ''} onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                  <div>
                    <label className="label">{t.accountNum}</label>
                    <input value={formData.bank_account || ''} onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label">{t.iban}</label>
                    <input value={formData.bank_iban || ''} onChange={(e) => setFormData({ ...formData, bank_iban: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ====== ACADEMIC TAB ====== */}
          {activeTab === 'academic' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                <AcademicIcon className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-800">{t.mySchoolYears.replace('{name}', settings?.name || t.mySchoolTitle)}</p>
                  <p className="text-sm text-blue-600">{t.mySchoolYearsDesc}</p>
                </div>
              </div>

              {currentAcademicYear && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">{t.activeYear}: {currentAcademicYear.label}</p>
                    <p className="text-sm text-green-600">{currentAcademicYear.start_date} &rarr; {currentAcademicYear.end_date}</p>
                  </div>
                </div>
              )}

              <div className="card">
                <div className="card-header flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <AcademicIcon className="w-5 h-5 text-primary-600" />
                    {t.academicYears}
                  </h3>
                  {canModify && (
                    <button onClick={openCreateAcademicYear} className="btn-primary text-sm">
                      <Plus className="w-4 h-4 mr-1" />{t.newYear}
                    </button>
                  )}
                </div>
                <div className="card-body">
                  {academicYearsLoading ? (
                    <div className="text-center py-4"><RefreshCw className="w-6 h-6 animate-spin text-gray-300 mx-auto" /></div>
                  ) : allAcademicYears.filter(y => y.school_id === userSchoolId).length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">{t.noYears}</p>
                  ) : (
                    <div className="space-y-2">
                      {allAcademicYears
                        .filter((year: AcademicYear) => year.school_id === userSchoolId)
                        .map((year: AcademicYear) => (
                        <div key={year.id} className={`flex items-center justify-between p-3 rounded-lg ${year.is_current ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-3">
                            <BookOpen className="w-5 h-5 text-primary-600" />
                            <div>
                              <p className="font-medium text-sm">{year.label}</p>
                              <p className="text-xs text-gray-500">{year.start_date} &rarr; {year.end_date}</p>
                              <p className="text-xs text-gray-400">
                                {t.gradingScale}: {year.grading_scale || 20} | {t.passThreshold}: {year.pass_threshold || 10} | {t.honorThreshold}: {year.honor_threshold || 12}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {year.is_current && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">{t.active}</span>
                            )}
                            {year.is_selected && !year.is_current && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{t.selected}</span>
                            )}
                            {canModify && (
                              <>
                                <button onClick={() => openEditAcademicYear(year)} className="p-1.5 hover:bg-gray-200 rounded-lg text-gray-500"><Edit2 className="w-4 h-4" /></button>
                                <button onClick={() => openDeleteYearModal(year)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="font-semibold flex items-center gap-2"><Calendar className="w-5 h-5 text-primary-600" />{t.configAcademic}</h3>
                </div>
                <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">{t.startDate}</label>
                    <input type="date" value={formData.academic_year_start || ''} onChange={(e) => setFormData({ ...formData, academic_year_start: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                  <div>
                    <label className="label">{t.endDate}</label>
                    <input type="date" value={formData.academic_year_end || ''} onChange={(e) => setFormData({ ...formData, academic_year_end: e.target.value })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                  <div>
                    <label className="label">{t.termsCount}</label>
                    <select value={formData.terms_count || 3} onChange={(e) => setFormData({ ...formData, terms_count: parseInt(e.target.value) })} className="input" disabled={isReadonly}>
                      <option value={2}>{t.semesters}</option>
                      <option value={3}>{t.trimesters}</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">{t.sequencesCount}</label>
                    <select value={formData.sequences_per_term || 2} onChange={(e) => setFormData({ ...formData, sequences_per_term: parseInt(e.target.value) })} className="input" disabled={isReadonly}>
                      <option value={1}>1</option>
                      <option value={2}>2</option>
                      <option value={3}>3</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="font-semibold flex items-center gap-2"><CheckCircle className="w-5 h-5 text-primary-600" />{t.gradingScale}</h3>
                </div>
                <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">{t.maxScale}</label>
                    <select value={formData.grading_scale || 20} onChange={(e) => setFormData({ ...formData, grading_scale: parseInt(e.target.value) })} className="input" disabled={isReadonly}>
                      <option value={20}>20</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">{t.passThreshold}</label>
                    <input type="number" step="0.5" value={formData.pass_threshold || 10} onChange={(e) => setFormData({ ...formData, pass_threshold: parseFloat(e.target.value) })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                  <div>
                    <label className="label">{t.honorThreshold}</label>
                    <input type="number" step="0.5" value={formData.honor_threshold || 12} onChange={(e) => setFormData({ ...formData, honor_threshold: parseFloat(e.target.value) })} className="input" disabled={isReadonly} readOnly={isReadonly} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ====== SCHOOLS TAB ====== */}
          {activeTab === 'schools' && (
            <div className="space-y-6">
              {!isSuperAdminRole && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                  <School className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-800">{t.mySchoolBanner}</p>
                    <p className="text-sm text-blue-600">
                      {isAdminRole || isProviseurRole ? t.mySchoolBannerAdmin : t.mySchoolBannerDesc}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary-600" />
                  {isSuperAdminRole ? t.schoolsTitle : t.mySchoolTitle}
                </h3>
                {canModify && isSuperAdminRole && (
                  <button onClick={openCreateSchool} className="btn-primary">
                    <Plus className="w-4 h-4 mr-1" />{t.newSchool}
                  </button>
                )}
              </div>
              {schoolsLoading ? (
                <div className="text-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-gray-300 mx-auto" /></div>
              ) : visibleSchools.length === 0 ? (
                <div className="card">
                  <div className="card-body text-center py-8">
                    <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{isSuperAdminRole ? t.noSchools : t.noSchoolsUser}</p>
                  </div>
                </div>
              ) : (
                <div className={`grid grid-cols-1 ${isSuperAdminRole ? 'md:grid-cols-2' : ''} gap-4`}>
                  {visibleSchools.map((school) => (
                    <div key={school.id} className="card">
                      <div className="card-body">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                              <School className="w-6 h-6 text-primary-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900">{school.name}</h4>
                              <p className="text-sm text-gray-500">{school.short_name || school.acronym}</p>
                            </div>
                          </div>
                          {canModify && isSuperAdminRole && (
                            <div className="flex items-center gap-1">
                              <button onClick={() => openEditSchool(school)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500">
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleDeleteSchool(school.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="mt-3 space-y-1 text-sm text-gray-600">
                          <p className="flex items-center gap-2"><MapPin className="w-3 h-3" />{school.address}, {school.city}, {school.country}</p>
                          <p className="flex items-center gap-2"><Phone className="w-3 h-3" />{school.phone || 'N/A'}</p>
                          <p className="flex items-center gap-2"><Mail className="w-3 h-3" />{school.email || 'N/A'}</p>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${school.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                            {school.status === 'active' ? t.active : t.inactive}
                          </span>
                          {userSchoolId === school.id && !isSuperAdminRole && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{t.mySchoolBadge}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="card">
                <div className="card-header flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <AcademicIcon className="w-5 h-5 text-primary-600" />
                    {isSuperAdminRole ? t.yearsAllSchools : isAdminRole || isProviseurRole ? t.yearsMySchool : t.myYear}
                  </h3>
                  {canModify && (
                    <button onClick={openCreateAcademicYear} className="btn-primary text-sm">
                      <Plus className="w-4 h-4 mr-1" />{t.newYear}
                    </button>
                  )}
                </div>
                <div className="card-body">
                  {academicYearsLoading ? (
                    <div className="text-center py-4"><RefreshCw className="w-6 h-6 animate-spin text-gray-300 mx-auto" /></div>
                  ) : visibleAcademicYears.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">{isSuperAdminRole ? t.noYears : t.noYearsAvailable}</p>
                  ) : (
                    <div className="space-y-2">
                      {visibleAcademicYears.map((year) => (
                        <div key={year.id} className={`flex items-center justify-between p-3 rounded-lg ${year.is_current ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-3">
                            <BookOpen className="w-5 h-5 text-primary-600" />
                            <div>
                              <p className="font-medium text-sm">{year.label}</p>
                              <p className="text-xs text-gray-500">{year.start_date} &rarr; {year.end_date}</p>
                              {isSuperAdminRole && (
                                <p className="text-xs text-gray-400">School ID: {year.school_id}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {year.is_current && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">{t.active}</span>
                            )}
                            {year.is_selected && !year.is_current && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{t.selected}</span>
                            )}
                            {canModify && (isSuperAdminRole || year.school_id === userSchoolId) && (
                              <>
                                {!year.is_current && (
                                  <button onClick={() => handleSetCurrentAcademicYear(year.id)} className="px-2 py-0.5 bg-primary-50 text-primary-600 rounded-full text-xs font-medium hover:bg-primary-100">{t.setActive}</button>
                                )}
                                <button onClick={() => openEditAcademicYear(year)} className="p-1.5 hover:bg-gray-200 rounded-lg text-gray-500"><Edit2 className="w-4 h-4" /></button>
                                <button onClick={() => openDeleteYearModal(year)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ====== NOTIFICATIONS TAB ====== */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div className="card">
                <div className="card-header"><h3 className="font-semibold flex items-center gap-2"><Bell className="w-5 h-5 text-primary-600" />{t.preferences}</h3></div>
                <div className="card-body space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div><p className="font-medium">{t.emailNotif}</p><p className="text-sm text-gray-500">{t.emailNotifDesc}</p></div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={formData.email_notifications ?? true} onChange={(e) => setFormData({ ...formData, email_notifications: e.target.checked })} className="sr-only peer" disabled={isReadonly} />
                      <div className={`w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 ${isReadonly ? 'opacity-60 cursor-not-allowed' : ''}`}></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div><p className="font-medium">{t.smsNotif}</p><p className="text-sm text-gray-500">{t.smsNotifDesc}</p></div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={formData.sms_notifications ?? false} onChange={(e) => setFormData({ ...formData, sms_notifications: e.target.checked })} className="sr-only peer" disabled={isReadonly} />
                      <div className={`w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 ${isReadonly ? 'opacity-60 cursor-not-allowed' : ''}`}></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ====== SECURITY TAB ====== */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <div className="card">
                <div className="card-header"><h3 className="font-semibold flex items-center gap-2"><Lock className="w-5 h-5 text-primary-600" />{t.password}</h3></div>
                <div className="card-body">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center"><Shield className="w-5 h-5 text-primary-600" /></div>
                      <div><p className="font-medium">{t.changePassword}</p><p className="text-sm text-gray-500">{t.changePasswordDesc}</p></div>
                    </div>
                    <button onClick={() => setShowPasswordModal(true)} className="btn-outline">{t.modify}</button>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header"><h3 className="font-semibold flex items-center gap-2"><Shield className="w-5 h-5 text-primary-600" />{t.twoFA}</h3></div>
                <div className="card-body">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        {twoFAStatus?.enabled ? <Shield className="w-5 h-5 text-green-600" /> : <Shield className="w-5 h-5 text-gray-400" />}
                      </div>
                      <div><p className="font-medium">{twoFAStatus?.enabled ? t.twoFAActive : t.twoFAInactive}</p><p className="text-sm text-gray-500">{twoFAStatus?.enabled ? t.twoFAProtected : t.twoFAActivate}</p></div>
                    </div>
                    {twoFAStatus?.enabled ? (
                      <button onClick={handleDisable2FA} className="btn-outline text-red-600 border-red-200 hover:bg-red-50">{t.disable}</button>
                    ) : (
                      <button onClick={handleSetup2FA} className="btn-primary">{t.activate}</button>
                    )}
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header"><h3 className="font-semibold flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-primary-600" />{t.maintenanceMode}</h3></div>
                <div className="card-body">
                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                    <div><p className="font-medium text-red-700">{t.maintenanceMode}</p><p className="text-sm text-red-600">{t.maintenanceDesc}</p></div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={formData.maintenance_mode ?? false} onChange={(e) => setFormData({ ...formData, maintenance_mode: e.target.checked })} className="sr-only peer" disabled={isReadonly} />
                      <div className={`w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600 ${isReadonly ? 'opacity-60 cursor-not-allowed' : ''}`}></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ====== APPEARANCE TAB ====== */}
          {activeTab === 'appearance' && (
            <div className="space-y-6">
              <div className="card">
                <div className="card-header"><h3 className="font-semibold flex items-center gap-2"><Palette className="w-5 h-5 text-primary-600" />{t.localization}</h3></div>
                <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">{t.language}</label>
                    <select value={formData.language || 'fr-en'} onChange={(e) => setFormData({ ...formData, language: e.target.value })} className="input" disabled={isReadonly}>
                      {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">{t.timezone}</label>
                    <select value={formData.timezone || 'Africa/Douala'} onChange={(e) => setFormData({ ...formData, timezone: e.target.value })} className="input" disabled={isReadonly}>
                      {TIMEZONES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">{t.currency}</label>
                    <select value={formData.currency || 'XAF'} onChange={(e) => setFormData({ ...formData, currency: e.target.value })} className="input" disabled={isReadonly}>
                      {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ====== SYSTEM TAB ====== */}
          {activeTab === 'system' && (
            <div className="space-y-6">
              <div className="card">
                <div className="card-header"><h3 className="font-semibold flex items-center gap-2"><Database className="w-5 h-5 text-primary-600" />{t.backups}</h3></div>
                <div className="card-body space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div><p className="font-medium">{t.autoBackup}</p><p className="text-sm text-gray-500">{t.autoBackupDesc}</p></div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={formData.auto_backup ?? true} onChange={(e) => setFormData({ ...formData, auto_backup: e.target.checked })} className="sr-only peer" disabled={isReadonly} />
                      <div className={`w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 ${isReadonly ? 'opacity-60 cursor-not-allowed' : ''}`}></div>
                    </label>
                  </div>
                  <div>
                    <label className="label">{t.frequency}</label>
                    <select value={formData.backup_frequency || 'weekly'} onChange={(e) => setFormData({ ...formData, backup_frequency: e.target.value })} className="input" disabled={isReadonly}>
                      {BACKUP_FREQUENCIES.map((f) => (
                        <option key={f.code} value={f.code}>{lang === 'en' ? f.label_en : f.label_fr}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setShowPasswordModal(false); setPasswordError('') }} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full animate-bounce-in">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{t.changePasswordTitle}</h3>
              <button onClick={() => { setShowPasswordModal(false); setPasswordError('') }} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <form onSubmit={handleChangePassword} className="p-6 space-y-4">
              {passwordError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />{passwordError}
                </div>
              )}
              <div><label className="label">{t.currentPassword}</label><input type="password" required value={passwordData.current_password} onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })} className="input" /></div>
              <div>
                <label className="label">{t.newPassword}</label>
                <div className="relative">
                  <input type={showNewPassword ? 'text' : 'password'} required value={passwordData.new_password} onChange={(e) => { setPasswordData({ ...passwordData, new_password: e.target.value }); setPasswordError('') }} className="input pr-10" />
                  <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                </div>
                <p className="text-xs text-gray-500 mt-1">{t.passwordHint}</p>
              </div>
              <div><label className="label">{t.confirmPassword}</label><input type="password" required value={passwordData.confirm_password} onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })} className="input" /></div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowPasswordModal(false); setPasswordError('') }} className="btn-outline">{t.cancel}</button>
                <button type="submit" className="btn-primary">{t.change}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* School Modal */}
      {showSchoolModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowSchoolModal(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-bounce-in">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{editingSchool ? t.editSchoolModal : t.newSchoolModal} {t.schoolModalTitle}</h3>
              <button onClick={() => setShowSchoolModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <form onSubmit={handleSaveSchool} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">{t.name} *</label><input required value={schoolFormData.name || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, name: e.target.value })} className="input" /></div>
                <div><label className="label">{t.shortName} *</label><input required value={schoolFormData.short_name || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, short_name: e.target.value })} className="input" placeholder="Ex: LYBIBAL" /></div>
              </div>
              <div><label className="label">{t.address}</label><input value={schoolFormData.address || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, address: e.target.value })} className="input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">{t.city}</label><input value={schoolFormData.city || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, city: e.target.value })} className="input" /></div>
                <div><label className="label">{t.country}</label><input value={schoolFormData.country || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, country: e.target.value })} className="input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">{t.phone}</label><input value={schoolFormData.phone || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, phone: e.target.value })} className="input" /></div>
                <div><label className="label">{t.email}</label><input type="email" value={schoolFormData.email || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, email: e.target.value })} className="input" /></div>
              </div>
              <div><label className="label">{t.website}</label><input value={schoolFormData.website || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, website: e.target.value })} className="input" placeholder={t.websitePlaceholder} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">{t.foundedYear}</label><input type="number" value={schoolFormData.founded_year || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, founded_year: parseInt(e.target.value) })} className="input" /></div>
                <div>
                  <label className="label">{t.status}</label>
                  <select value={schoolFormData.status || 'active'} onChange={(e) => setSchoolFormData({ ...schoolFormData, status: e.target.value })} className="input">
                    <option value="active">{t.active}</option>
                    <option value="inactive">{t.inactive}</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">{t.director}</label><input value={schoolFormData.director_name || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, director_name: e.target.value })} className="input" /></div>
                <div><label className="label">{t.directorEmailLabel}</label><input type="email" value={schoolFormData.director_email || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, director_email: e.target.value })} className="input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">{t.accreditationLabel}</label><input value={schoolFormData.accreditation_number || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, accreditation_number: e.target.value })} className="input" /></div>
                <div><label className="label">{t.taxLabel}</label><input value={schoolFormData.tax_id || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, tax_id: e.target.value })} className="input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">{t.bankLabel}</label><input value={schoolFormData.bank_name || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, bank_name: e.target.value })} className="input" /></div>
                <div><label className="label">{t.accountLabel}</label><input value={schoolFormData.bank_account || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, bank_account: e.target.value })} className="input" /></div>
              </div>
              <div><label className="label">{t.iban}</label><input value={schoolFormData.bank_iban || ''} onChange={(e) => setSchoolFormData({ ...schoolFormData, bank_iban: e.target.value })} className="input" /></div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowSchoolModal(false)} className="btn-outline">{t.cancel}</button>
                <button type="submit" className="btn-primary">{editingSchool ? t.update : t.create}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Academic Year Modal */}
      {showAcademicYearModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAcademicYearModal(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-bounce-in">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{editingAcademicYear ? t.editYearModal : t.newYearModal} {t.yearModalTitle}</h3>
              <button onClick={() => setShowAcademicYearModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <form onSubmit={handleSaveAcademicYear} className="p-6 space-y-4">
              <div><label className="label">{t.label} *</label>
              <input required maxLength={20} value={academicYearFormData.label} onChange={(e) => setAcademicYearFormData({ ...academicYearFormData, label: e.target.value })} className="input" placeholder={t.labelPlaceholder} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">{t.startDateLabel} *</label><input type="date" required value={academicYearFormData.start_date} onChange={(e) => setAcademicYearFormData({ ...academicYearFormData, start_date: e.target.value })} className="input" /></div>
                <div><label className="label">{t.endDateLabel} *</label><input type="date" required value={academicYearFormData.end_date} onChange={(e) => setAcademicYearFormData({ ...academicYearFormData, end_date: e.target.value })} className="input" /></div>
              </div>
              <div>
                <label className="label">{t.school}</label>
                <select value={academicYearFormData.school_id} onChange={(e) => setAcademicYearFormData({ ...academicYearFormData, school_id: parseInt(e.target.value) })} className="input">
                  {isSuperAdminRole 
                    ? schools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)
                    : visibleSchools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)
                  }
                </select>
              </div>

              {/* ====== DUPLICATION SECTION (only on create) ====== */}
              {!editingAcademicYear && (
                <div className="border-t border-gray-200 pt-4 space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <h4 className="font-medium text-blue-800 text-sm flex items-center gap-2">
                      <RefreshCw className="w-4 h-4" />{t.copyFromPrevious}
                    </h4>
                    <p className="text-xs text-blue-600 mt-1">{t.duplicateYearDesc}</p>
                  </div>

                  <div>
                    <label className="label">{t.sourceYear}</label>
                    <select 
                      value={academicYearFormData.source_year_id ?? ''} 
                      onChange={(e) => setAcademicYearFormData({ ...academicYearFormData, source_year_id: e.target.value ? Number(e.target.value) : null })} 
                      className="input"
                    >
                      <option value="">{t.generateNone}</option>
                      {allAcademicYears
                        .filter((y: AcademicYear) => y.school_id === (academicYearFormData.school_id || schoolId))
                        .map((y: AcademicYear) => (
                          <option key={y.id} value={y.id}>{y.label} ({y.start_date} &rarr; {y.end_date})</option>
                        ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">{t.sourceYearHint}</p>
                  </div>

                  {academicYearFormData.source_year_id && (
                    <>
                      {/* Elements to reproduce */}
                      <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                        <h5 className="text-xs font-bold uppercase text-gray-500 tracking-wider">{t.copyElements}</h5>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={academicYearFormData.copy_teachers} onChange={(e) => setAcademicYearFormData({...academicYearFormData, copy_teachers: e.target.checked})} />
                            {t.copyTeachers}
                          </label>
                          <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={academicYearFormData.copy_subjects} onChange={(e) => setAcademicYearFormData({...academicYearFormData, copy_subjects: e.target.checked})} />
                            {t.copySubjects}
                          </label>
                          <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={academicYearFormData.copy_classes} onChange={(e) => setAcademicYearFormData({...academicYearFormData, copy_classes: e.target.checked})} />
                            {t.copyClasses}
                          </label>
                          <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={academicYearFormData.copy_assignments} onChange={(e) => setAcademicYearFormData({...academicYearFormData, copy_assignments: e.target.checked})} />
                            {t.copyAssignments}
                          </label>
                        </div>
                      </div>

                      {/* ✅ NOUVEAU : Classes permanentes et gestion des doublons */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3">
                        <h5 className="text-xs font-bold uppercase text-blue-600 tracking-wider flex items-center gap-2">
                          <School className="w-3 h-3" />{t.permanentClasses}
                        </h5>

                        {/* Skip permanent classes */}
                        <label className="flex items-start gap-2 text-sm">
                          <input 
                            type="checkbox" 
                            checked={academicYearFormData.skip_permanent_classes} 
                            onChange={(e) => setAcademicYearFormData({...academicYearFormData, skip_permanent_classes: e.target.checked})} 
                            className="w-4 h-4 text-blue-600 rounded mt-0.5"
                          />
                          <div>
                            <span className="font-medium text-gray-700">{t.skipPermanentClasses}</span>
                            <p className="text-xs text-gray-500 mt-0.5">{t.skipPermanentClassesHelp}</p>
                          </div>
                        </label>

                        {/* Duplicate strategy */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700">{t.duplicateStrategy}</label>
                          <p className="text-xs text-gray-500">{t.duplicateStrategyHelp}</p>
                          <div className="flex flex-col gap-2">
                            <label className="flex items-center gap-2 text-sm p-2 bg-white rounded border border-gray-200 hover:border-blue-300 transition-colors cursor-pointer">
                              <input 
                                type="radio" 
                                name="duplicate_strategy" 
                                value="skip" 
                                checked={academicYearFormData.duplicate_strategy === 'skip'} 
                                onChange={() => setAcademicYearFormData({...academicYearFormData, duplicate_strategy: 'skip'})} 
                                className="w-4 h-4 text-blue-600"
                              />
                              <span>{t.duplicateStrategySkip}</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm p-2 bg-white rounded border border-gray-200 hover:border-blue-300 transition-colors cursor-pointer">
                              <input 
                                type="radio" 
                                name="duplicate_strategy" 
                                value="merge" 
                                checked={academicYearFormData.duplicate_strategy === 'merge'} 
                                onChange={() => setAcademicYearFormData({...academicYearFormData, duplicate_strategy: 'merge'})} 
                                className="w-4 h-4 text-blue-600"
                              />
                              <span>{t.duplicateStrategyMerge}</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm p-2 bg-white rounded border border-gray-200 hover:border-blue-300 transition-colors cursor-pointer">
                              <input 
                                type="radio" 
                                name="duplicate_strategy" 
                                value="overwrite" 
                                checked={academicYearFormData.duplicate_strategy === 'overwrite'} 
                                onChange={() => setAcademicYearFormData({...academicYearFormData, duplicate_strategy: 'overwrite'})} 
                                className="w-4 h-4 text-blue-600"
                              />
                              <span>{t.duplicateStrategyOverwrite}</span>
                            </label>
                          </div>
                        </div>

                        {/* Info message */}
                        {academicYearFormData.skip_permanent_classes && academicYearFormData.duplicate_strategy === 'skip' && (
                          <div className="flex items-start gap-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                            <CheckCircle className="w-3 h-3 mt-0.5 shrink-0" />
                            {t.noDuplicateWarning}
                          </div>
                        )}
                      </div>

                      {/* Timetable periods */}
                      <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                        <h5 className="text-xs font-bold uppercase text-gray-500 tracking-wider">{t.generatePeriods}</h5>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 text-sm">
                            <input type="radio" name="generate_periods" value="none" checked={academicYearFormData.generate_periods === 'none'} onChange={() => setAcademicYearFormData({...academicYearFormData, generate_periods: 'none'})} />
                            {t.generateNone}
                          </label>
                          <label className="flex items-center gap-2 text-sm">
                            <input type="radio" name="generate_periods" value="weeks" checked={academicYearFormData.generate_periods === 'weeks'} onChange={() => setAcademicYearFormData({...academicYearFormData, generate_periods: 'weeks'})} />
                            {t.generateWeeks}
                          </label>
                          <label className="flex items-center gap-2 text-sm">
                            <input type="radio" name="generate_periods" value="months" checked={academicYearFormData.generate_periods === 'months'} onChange={() => setAcademicYearFormData({...academicYearFormData, generate_periods: 'months'})} />
                            {t.generateMonths}
                          </label>
                        </div>
                      </div>

                      {/* Parameters to carry over */}
                      <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                        <h5 className="text-xs font-bold uppercase text-gray-500 tracking-wider">{t.copyConfig}</h5>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={academicYearFormData.copy_fees} onChange={(e) => setAcademicYearFormData({...academicYearFormData, copy_fees: e.target.checked})} />
                            {t.copyFees}
                          </label>
                          {academicYearFormData.copy_fees && (
                            <label className="flex items-center gap-2 text-sm ml-6 text-gray-600">
                              <input type="checkbox" checked={academicYearFormData.overwrite_fees} onChange={(e) => setAcademicYearFormData({...academicYearFormData, overwrite_fees: e.target.checked})} />
                              {t.overwriteExisting}
                            </label>
                          )}

                          <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={academicYearFormData.copy_programs} onChange={(e) => setAcademicYearFormData({...academicYearFormData, copy_programs: e.target.checked})} />
                            {t.copyPrograms}
                          </label>
                          {academicYearFormData.copy_programs && (
                            <label className="flex items-center gap-2 text-sm ml-6 text-gray-600">
                              <input type="checkbox" checked={academicYearFormData.overwrite_programs} onChange={(e) => setAcademicYearFormData({...academicYearFormData, overwrite_programs: e.target.checked})} />
                              {t.overwriteExisting}
                            </label>
                          )}

                          <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={academicYearFormData.copy_quotas} onChange={(e) => setAcademicYearFormData({...academicYearFormData, copy_quotas: e.target.checked})} />
                            {t.copyQuotas}
                          </label>
                          {academicYearFormData.copy_quotas && (
                            <label className="flex items-center gap-2 text-sm ml-6 text-gray-600">
                              <input type="checkbox" checked={academicYearFormData.overwrite_quotas} onChange={(e) => setAcademicYearFormData({...academicYearFormData, overwrite_quotas: e.target.checked})} />
                              {t.overwriteExisting}
                            </label>
                          )}

                          <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={academicYearFormData.copy_weights} onChange={(e) => setAcademicYearFormData({...academicYearFormData, copy_weights: e.target.checked})} />
                            {t.copyWeights}
                          </label>
                          {academicYearFormData.copy_weights && (
                            <label className="flex items-center gap-2 text-sm ml-6 text-gray-600">
                              <input type="checkbox" checked={academicYearFormData.overwrite_weights} onChange={(e) => setAcademicYearFormData({...academicYearFormData, overwrite_weights: e.target.checked})} />
                              {t.overwriteExisting}
                            </label>
                          )}

                          <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={academicYearFormData.copy_formulas} onChange={(e) => setAcademicYearFormData({...academicYearFormData, copy_formulas: e.target.checked})} />
                            {t.copyFormulas}
                          </label>
                          {academicYearFormData.copy_formulas && (
                            <label className="flex items-center gap-2 text-sm ml-6 text-gray-600">
                              <input type="checkbox" checked={academicYearFormData.overwrite_formulas} onChange={(e) => setAcademicYearFormData({...academicYearFormData, overwrite_formulas: e.target.checked})} />
                              {t.overwriteExisting}
                            </label>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-medium text-sm text-gray-700 mb-3 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary-600" />{t.academicConfig}</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">{t.maxScale}</label>
                    <select value={academicYearFormData.grading_scale} onChange={(e) => setAcademicYearFormData({ ...academicYearFormData, grading_scale: parseInt(e.target.value) })} className="input">
                      <option value={20}>20</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">{t.passThreshold}</label>
                    <input type="number" step="0.5" value={academicYearFormData.pass_threshold} onChange={(e) => setAcademicYearFormData({ ...academicYearFormData, pass_threshold: parseFloat(e.target.value) })} className="input" />
                  </div>
                  <div>
                    <label className="label">{t.honorThreshold}</label>
                    <input type="number" step="0.5" value={academicYearFormData.honor_threshold} onChange={(e) => setAcademicYearFormData({ ...academicYearFormData, honor_threshold: parseFloat(e.target.value) })} className="input" />
                  </div>
                  <div>
                    <label className="label">{t.termsCount}</label>
                    <select value={academicYearFormData.terms_count} onChange={(e) => setAcademicYearFormData({ ...academicYearFormData, terms_count: parseInt(e.target.value) })} className="input">
                      <option value={2}>{t.semesters}</option>
                      <option value={3}>{t.trimesters}</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">{t.sequencesCount}</label>
                    <select value={academicYearFormData.sequences_per_term} onChange={(e) => setAcademicYearFormData({ ...academicYearFormData, sequences_per_term: parseInt(e.target.value) })} className="input">
                      <option value={1}>1</option>
                      <option value={2}>2</option>
                      <option value={3}>3</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <input type="checkbox" id="is_current" checked={academicYearFormData.is_current} onChange={(e) => setAcademicYearFormData({ ...academicYearFormData, is_current: e.target.checked })} className="w-4 h-4 text-primary-600 rounded" />
                <label htmlFor="is_current" className="text-sm font-medium">{t.setCurrent}</label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAcademicYearModal(false)} className="btn-outline">{t.cancel}</button>
                <button type="submit" className="btn-primary">{editingAcademicYear ? t.update : t.create}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2FA Setup Modal */}
      {show2FAModal && twoFASetup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShow2FAModal(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full animate-bounce-in">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{t.twoFAModalTitle}</h3>
              <button onClick={() => setShow2FAModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-4 text-center">
              <p className="text-sm text-gray-600">{t.twoFAScan}</p>
              {twoFASetup?.qr_code && <img src={twoFASetup.qr_code} alt="QR Code" className="mx-auto w-48 h-48" />}
              <div className="bg-gray-100 p-3 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">{t.secretKey}</p>
                <code className="text-sm font-mono">{twoFASetup?.secret || ''}</code>
              </div>
              <div>
                <label className="label">{t.verifyCode}</label>
                <input type="text" maxLength={6} placeholder={t.codePlaceholder} value={twoFACode} onChange={(e) => setTwoFACode(e.target.value)} className="input text-center text-2xl tracking-widest" />
              </div>
              <button onClick={handleEnable2FA} className="btn-primary w-full">{t.enable2FA}</button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ MODAL DE SUPPRESSION ANNEE SCOLAIRE — Design moderne */}
      {showDeleteYearModal && deleteYearTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" 
            onClick={closeDeleteYearModal} 
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full animate-bounce-in overflow-hidden">
            {/* Header rouge */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{t.confirmDeleteYear}</h3>
                  <p className="text-sm text-red-100">{t.deleteYearWarning || 'Cette action est irreversible.'}</p>
                </div>
              </div>
            </div>

            {/* Corps */}
            <div className="p-6 space-y-4">
              {/* Détails de l'année */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{deleteYearTarget.label}</p>
                    <p className="text-sm text-gray-500">
                      {deleteYearTarget.start_date} &rarr; {deleteYearTarget.end_date}
                    </p>
                    {deleteYearTarget.is_current && (
                      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <CheckCircle className="w-3 h-3" />{t.active}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Avertissements */}
              <div className="space-y-2">
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-700">
                    {t.deleteYearConsequences || 'Toutes les donnees associees (classes, matieres, notes, emplois du temps) seront definitivement supprimees.'}
                  </p>
                </div>
                {deleteYearTarget.is_current && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">
                      {t.deleteCurrentYearWarning || 'Cette annee est actuellement active. Sa suppression desactivera toutes les operations scolaires.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Checkbox de confirmation */}
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input 
                  type="checkbox" 
                  className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  onChange={(e) => {
                    // La checkbox force l'utilisateur à confirmer
                    const btn = document.getElementById('confirm-delete-year-btn') as HTMLButtonElement
                    if (btn) btn.disabled = !e.target.checked
                  }}
                />
                <span>{t.confirmDeleteYearCheckbox || 'Je comprends que cette action est irreversible.'}</span>
              </label>
            </div>

            {/* Footer boutons */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button 
                onClick={closeDeleteYearModal} 
                disabled={isDeletingYear}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t.cancel}
              </button>
              <button 
                id="confirm-delete-year-btn"
                onClick={handleConfirmDeleteYear} 
                disabled={isDeletingYear}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 transition-all shadow-md shadow-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeletingYear ? (
                  <><RefreshCw className="w-4 h-4 animate-spin" />{t.deleting || 'Suppression...'}</>
                ) : (
                  <><Trash2 className="w-4 h-4" />{t.confirmDelete || 'Supprimer'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}